
import Cleave from 'cleave.js'

export default {
  inject: {
    formControlIds: {
      default: () => ({}),
    },
  },
  inheritAttrs: false,
  props: {
    value: {
      type: Number,
      default: 0,
    },
    id: {
      type: String,
      default: null,
    },
    allowCents: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    size: {
      type: [Number, String],
      default: 4,
    },
    min: {
      type: Number,
      default: 0,
    },
    rangeUnderflowMessage: {
      type: String,
      default: null,
    },
    max: {
      type: Number,
      default: null,
    },
    rangeOverflowMessage: {
      type: String,
      default: null,
    },
    hideSymbol: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    maxRounded() {
      return this.max !== null ? this.getRoundedNumber(this.max) : null
    },
    minRounded() {
      return this.min !== null ? this.getRoundedNumber(this.min) : null
    },
  },

  watch: {
    value(newValue) {
      if (newValue.toString() !== this.cleave.getRawValue()) {
        this.setValidity(newValue)

        this.cleave.setRawValue(this.getRoundedString(newValue))
      }
    },
    allowCents() {
      this.recreateCleaveInstance()
    },
    '$i18n.locale': function () {
      this.recreateCleaveInstance()
    },
  },

  mounted() {
    this.recreateCleaveInstance()
    this.$nextTick(() => {
      this.setValidity(this.value)
    })
  },

  destroyed() {
    if (this.cleave) {
      this.cleave.destroy()
    }
  },

  methods: {
    setValidity(value) {
      if (this.min !== null && value < this.minRounded) {
        this.$refs.input.setCustomValidity(
          this.rangeUnderflowMessage ??
            `Value must be greater than or equal to ${this.minRounded}.`,
        )
      } else if (this.max !== null && value > this.maxRounded) {
        this.$refs.input.setCustomValidity(
          this.rangeOverflowMessage ??
            `Value must be less than or equal to ${this.maxRounded}.`,
        )
      } else {
        this.$refs.input.setCustomValidity('')
      }
    },

    getRoundedNumber(val) {
      return this.allowCents ? Math.round(val * 100) / 100 : Math.floor(val)
    },

    getRoundedString(val) {
      return this.allowCents ? val.toFixed(2) : Math.floor(val).toString()
    },

    recreateCleaveInstance() {
      if (this.cleave) {
        this.cleave.destroy()
      }

      const { decimalSep, thousandSep } = this.getSeparators()

      this.cleave = new Cleave(this.$refs.input, {
        numeral: true,
        numeralThousandsGroupStyle: 'thousand',
        numeralDecimalMark: decimalSep,
        delimiter: thousandSep,
        numeralDecimalScale: this.allowCents ? 2 : 0,
        numeralPositiveOnly: true,
        onValueChanged: this.onValueChanged.bind(this),
      })

      this.cleave.setRawValue(this.getRoundedString(this.value))
    },

    getSeparators() {
      const parts = new Intl.NumberFormat(this.$i18n.locale).formatToParts(
        9999.99,
      )

      return {
        thousandSep: parts.find((part) => part.type === 'group').value,
        decimalSep: parts.find((part) => part.type === 'decimal').value,
      }
    },

    /**
     * This logic prevents the user from clearing the textfield leaving a invalid value in its place
     */
    onBlur() {
      const newValue = parseFloat(this.cleave.getRawValue())

      if (Number.isNaN(newValue)) {
        this.cleave.setRawValue(this.getRoundedString(this.min))
      }

      this.$emit('blur')
      this.$emit('change')
    },

    onKeyDown(event) {
      const value = parseFloat(this.cleave.getRawValue())

      // eslint-disable-next-line eqeqeq
      if (event.keyCode == 38) {
        // Up arrow
        if (!Number.isNaN(value)) {
          const newValue = value + this.calculateTickSize(value)

          this.cleave.setRawValue(this.getRoundedString(newValue))
        }
        // eslint-disable-next-line eqeqeq
      } else if (event.keyCode == 40) {
        // Down arrow
        if (!Number.isNaN(value) && value > 0) {
          const newValue = value - this.calculateTickSize(value)

          this.cleave.setRawValue(this.getRoundedString(newValue))
        }
      }

      // eslint-disable-next-line eqeqeq
      if (event.keyCode == 38 || event.keyCode == 40) {
        event.preventDefault()
        event.stopPropagation()
      }
    },

    onValueChanged() {
      const newValue = parseFloat(this.cleave.getRawValue())
      if (this.value !== newValue && !Number.isNaN(newValue)) {
        this.setValidity(newValue)
        this.$emit('input', newValue)
      }
    },

    calculateTickSize(minPrice) {
      let stepSize = 1
      if (minPrice < 10) {
        stepSize = 0.5
      } else if (minPrice < 100) {
        stepSize = 1
      } else if (minPrice < 1000) {
        stepSize = 10
      } else if (minPrice < 5000) {
        stepSize = 100
      } else if (minPrice < 10000) {
        stepSize = 200
      } else {
        stepSize = 250
      }
      return stepSize
    },
  },
}
